import '../styles/StartPage.css'
import { useEffect, useMemo, useRef, useState } from 'react';
import { FaPen } from 'react-icons/fa';
import { useStartPageContent, DEFAULT_STARTPAGE_CONTENT } from '../../ViewModel/StartPageViewModel.js';
import { sanitizeNavigationHref } from '../../Model/Services/urlSafetyService.js';

// Static background image served from public/Images without any dynamic fetching.
const STATIC_BACKGROUND_IMAGE_AVIF = '/Images/works.avif';
const STATIC_BACKGROUND_IMAGE_JPG = '/Images/works.jpg';

function StartPage({ adminMode = false }) {
    const { content, loading, error, saving, saveContent } = useStartPageContent();
    const hero = useMemo(() => {
        if (!content) return null;
        const heroContent = { ...DEFAULT_STARTPAGE_CONTENT, ...content };
        return { ...heroContent, backgroundImage: STATIC_BACKGROUND_IMAGE_JPG };
    }, [content]);
    const [form, setForm] = useState(hero ?? DEFAULT_STARTPAGE_CONTENT);
    const [message, setMessage] = useState('');
    const [editing, setEditing] = useState(false);
    const [displayBackgroundImage] = useState(STATIC_BACKGROUND_IMAGE_JPG);
    const backgroundRef = useRef(null);

    useEffect(() => {
        if (hero) setForm(hero);
    }, [hero]);

    // Background image is static, so no extra preload/caching logic needed.

    const handleChange = (e) => {
        const { name, value } = e.target;
        setForm((prev) => ({ ...prev, [name]: value }));
    };

    const handleSave = async () => {
        setMessage('');
        try {
            await saveContent(form);
            setMessage('Sparat.');
            setEditing(false);
        } catch (err) {
            setMessage(`Fel: ${err.message}`);
        }
    };

    const backgroundStyle = {
        backgroundImage: `linear-gradient(180deg, rgba(29, 25, 43, 0.88) 6%, rgba(30, 30, 30, 0.88) 90%), image-set(url('${STATIC_BACKGROUND_IMAGE_AVIF}') type('image/avif') 1x, url('${displayBackgroundImage}') type('image/jpeg') 1x)`,
    };

    const renderHero = () => (
        <div className="hero-stack">
            <div className="hero-tagline-row">
                <h4>{hero.tagline}</h4>
                {adminMode && (
                    <button className="hero-pen" aria-label="Redigera hero" onClick={() => setEditing(true)}>
                        <FaPen />
                    </button>
                )}
            </div>
            <h1>{hero.headline}</h1>
            <h2>{hero.subheadline}</h2>
            <a className="btn-contact" href={sanitizeNavigationHref(hero.buttonHref, '#kontakt')}>
                {hero.buttonText}
            </a>
        </div>
    );

    const renderEditor = () => (
        <div className="hero-edit-form">
            <label>
                Liten rubrik (h4)
                <input name="tagline" value={form.tagline} onChange={handleChange} />
            </label>
            <label>
                Rubrik (h1)
                <input name="headline" value={form.headline} onChange={handleChange} />
            </label>
            <label>
                Underrubrik (h2)
                <textarea name="subheadline" value={form.subheadline} onChange={handleChange} rows={3} />
            </label>
            <div className="hero-row">
                <label>
                    Knapptext
                    <input name="buttonText" value={form.buttonText} onChange={handleChange} />
                </label>
                <label>
                    Knapp-länk
                    <input name="buttonHref" value={form.buttonHref} onChange={handleChange} />
                </label>
            </div>
            <div className="hero-actions">
                <button className="primary" onClick={handleSave} disabled={saving}>
                    {saving ? 'Sparar...' : 'Spara'}
                </button>
                {message && <span className="hero-message">{message}</span>}
            </div>
        </div>
    );

    if (!hero) {
        return (
            <div className="background-img" style={backgroundStyle} ref={backgroundRef}>
                <div className="overlay">
                    {error ? (
                        <p className="hero-error">{error}</p>
                    ) : (
                        <p className="hero-loading">{loading ? 'Laddar...' : 'Innehåll saknas'}</p>
                    )}
                </div>
            </div>
        );
    }

    return (
        <div className="background-img" style={backgroundStyle} ref={backgroundRef}>
            <div className="overlay">
                {adminMode && editing ? renderEditor() : renderHero()}
                {error && <p className="hero-error">{error}</p>}
                {loading && !content && <p className="hero-loading">Laddar...</p>}
            </div>
        </div>
    );
}


export default StartPage;
